// Мэргэжилтний чат — өгөгдлийн давхарга.
//
// Мэргэжилтэн ямар ч сургуульд харьяалагдахгүй тул түүний бүх өрөө
// root `chatRooms` цуглуулгад байрладаг. Гар утасны апп (GlobalChatService)
// яг ижил зам, ижил талбарууд бичдэг тул хоёр тал нэг чатыг хардаг.
//
//   chatRooms/{roomId}                 — өрөө
//   chatRooms/{roomId}/messages/{id}   — мессеж
//
// Сургуулийн чат (`src/chat/`) энэ файлаас огт хамаарахгүй.

const fb = require("@/firebaseConfig.js");

// Удирдлага, ажилчид — багш тусдаа жагсаалт (апптай ижил).
const STAFF_ROLES = [
  "schooldirector",
  "schoolmanager",
  "schoolsocialworker",
  "schooldoctor",
  "schoolmealcontroller",
  "schoolaccounter",
  "schoolaccounter2",
];

export const ROLE_LABELS = {
  schooldirector: "Захирал",
  schoolmanager: "Менежер",
  schoolsocialworker: "Нийгмийн ажилтан",
  schooldoctor: "Эмч",
  schoolmealcontroller: "Хоол зүйч",
  schoolaccounter: "Нягтлан",
  teacher: "Багш",
  student: "Сурагч",
  citymanager: "Мэргэжилтэн",
};

/** «23-р сургууль · 12б» — сурагчийн хувьд анги нь хамт. */
export function whereFrom(person) {
  return [person.schoolName, person.className].filter(Boolean).join(" · ");
}

/**
 * Оролцогчийн мөр. Сургуулийн нэр өөрчлөгддөг тул `schoolId`, `schoolPath`
 * хамт бичигдэнэ — шүүлт, холбоос эдгээрээр найдвартай ажиллана.
 */
export function infoOf(person, year) {
  const info = {
    name: person.name,
    role: roleLabel(person.role),
    school: whereFrom(person),
    userPath: userPathOf(person, year),
  };
  // Зураг URL биш PERSON_ID — файл `user_avatars/{PERSON_ID}.jpg` нэртэй
  // тул зураг солигдоход хуучирдаггүй.
  if (person.personId) info.personId = person.personId;
  if (person.schoolPath) {
    info.schoolPath = person.schoolPath;
    info.schoolId = String(person.schoolPath).split("/").pop();
  }
  return info;
}

/** Профайл зураг — нэг хүнийг нэг л удаа уншина. */
const photoCache = {};

export async function photoOfPath(path) {
  if (!path) return "";
  if (photoCache[path] !== undefined) return photoCache[path];
  try {
    const doc = await fb.db.doc(path).get();
    const data = doc.exists ? doc.data() || {} : {};
    photoCache[path] = data.photoURL || data.PARENT_PHOTO_URL || "";
  } catch (e) {
    photoCache[path] = "";
  }
  return photoCache[path];
}

/** `user_avatars/{key}.jpg` — байхгүй бол хоосон. */
async function photoFromStorage(key) {
  if (!key) return "";
  const path = `user_avatars/${key}.jpg`;
  if (photoCache[path] !== undefined) return photoCache[path];
  try {
    photoCache[path] = await fb.storageRef.child(path).getDownloadURL();
  } catch (e) {
    photoCache[path] = "";
  }
  return photoCache[path];
}

/** Өрөөний оролцогчийн зураг: бичигдсэн нь, эсвэл зам, эсвэл сургуулиас. */
export async function photoForParticipant(info, id, year, schools) {
  // Зургийн файл `user_avatars/{PERSON_ID}.jpg` нэртэй — шууд асууна,
  // зураг солигдсон ч үргэлж шинийг нь өгнө.
  const personId = (info || {}).personId;
  if (personId) {
    const direct = await photoFromStorage(personId);
    if (direct) return direct;
  }
  const byId = await photoFromStorage(id);
  if (byId) return byId;
  // Хуучин өрөөнд бичигдсэн URL — сүүлчийн найдвар.
  const stored = (info || {}).photoURL;
  if (stored) return stored;
  const userPath = (info || {}).userPath;
  if (userPath) return photoOfPath(userPath);
  const schoolPath = (info || {}).schoolPath;
  if (schoolPath) {
    const staff = await photoOfPath(`${schoolPath}/teachers/${id}`);
    if (staff) return staff;
    return photoOfPath(`${schoolPath}/students-${year}/${id}`);
  }
  const root = await photoOfPath(`teachers/${id}`);
  if (root) return root;
  // Хуучин өрөөнд сургууль нь бичигдээгүй — аймгийн сургуулиудаас хайна.
  for (const school of schools || []) {
    const staff = await photoOfPath(`${school.ref.path}/teachers/${id}`);
    if (staff) return staff;
    const student = await photoOfPath(
      `${school.ref.path}/students-${year}/${id}`
    );
    if (student) return student;
  }
  return "";
}

/**
 * Хүний баримтын зам: ажилтан, багш `teachers`, сурагч `students-{жил}`,
 * мэргэжилтэн root `teachers` дотор.
 */
export function userPathOf(person, year) {
  if (!person.schoolPath) return `teachers/${person.id}`;
  const collection =
    String(person.role || "").toLowerCase() === "student"
      ? `students-${year}`
      : "teachers";
  return `${person.schoolPath}/${collection}/${person.id}`;
}

/**
 * Өмнө үүссэн өрөөнүүдэд сургуулийн ref байхгүй — нэрээр нь тааруулж нэг
 * удаа нөхөж бичнэ. Ижил нэртэй сургууль олдохгүй бол алгасна.
 */
const backfilled = new Set();

export async function backfillSchoolRefs(rooms, schools, myId, year, cityName) {
  // Сургуулийн жагсаалт ирээгүй байсан ч өөрийн мөрөө нөхөж болно.
  const byName = new Map(schools.map((school) => [school.name, school]));

  for (const room of rooms) {
    if (backfilled.has(room.id)) continue;
    backfilled.add(room.id);

    // Группын гишүүд members дэд цуглуулгад — өрөөний массивыг өсгөхгүй.
    if (room.type !== "direct") continue;

    const info = room.participantInfo || {};
    const updates = {};
    for (const id of Object.keys(info)) {
      const one = info[id] || {};

      // Мэргэжилтэн (өөрөө ч, бусад ч) root `teachers` дотор.
      if (id === myId || one.role === "Мэргэжилтэн") {
        if (!one.userPath) {
          updates[`participantInfo.${id}.userPath`] = `teachers/${id}`;
        }
        // Сургуулийн тал «Мэргэжилтэн · Өмнөговь» гэж харах ёстой.
        if (id === myId && !one.school && cityName) {
          updates[`participantInfo.${id}.school`] = cityName;
        }
        continue;
      }

      const name = String(one.school || "").split(" · ")[0];
      const school = byName.get(name);
      if (!school) continue;

      if (!one.schoolId) {
        updates[`participantInfo.${id}.schoolId`] = school.ref.id;
        updates[`participantInfo.${id}.schoolPath`] = school.ref.path;
      }
      if (!one.userPath) {
        const collection =
          one.role === "Сурагч" ? `students-${year}` : "teachers";
        updates[`participantInfo.${id}.userPath`] =
          `${school.ref.path}/${collection}/${id}`;
      }
    }

    // Хамгийн хуучин өрөөнүүдэд миний мөр огт байхгүй байж болно.
    if (!info[myId]) {
      updates[`participantInfo.${myId}.role`] = "Мэргэжилтэн";
      updates[`participantInfo.${myId}.userPath`] = `teachers/${myId}`;
      if (cityName) updates[`participantInfo.${myId}.school`] = cityName;
    }

    if (!Object.keys(updates).length) continue;
    try {
      await room.ref.update(updates);
    } catch (e) {
      // Дүрэм хаасан бол чимээгүй өнгөрнө — харагдац өөрчлөгдөхгүй.
    }
  }
}

/**
 * Мэргэжилтний мөр. Сургуулийн талын хүн «Мэргэжилтэн · Өмнөговь» гэж
 * хардаг тул аймгаа энд бичнэ — өөрийнх нь жагсаалтад энэ мөр гардаггүй.
 */
export function myInfo(me) {
  const info = {
    name: me.name,
    role: "Мэргэжилтэн",
    school: me.cityName || "",
    userPath: `teachers/${me.id}`,
  };
  if (me.personId) info.personId = me.personId;
  return info;
}

export function roleLabel(role) {
  return ROLE_LABELS[String(role || "").toLowerCase()] || "Ажилтан";
}

/**
 * Аймаг/нийслэлийн мэргэжилтэн. Зөвхөн эрхээр нь шийднэ — зарим
 * мэргэжилтний бүртгэлд `school` талбар үлдсэн байдаг тул түүнээс
 * хамааруулбал сургуулийн чат руу андуурч ордог.
 */
export function isSpecialist(userData) {
  if (!userData) return false;
  const role = String(userData.role || "").toLowerCase();
  return role === "citymanager" || role === "admin";
}

export function isNationalAdmin(userData) {
  const role = String((userData || {}).role || "").toLowerCase();
  return role === "superadmin" || role === "superadmin2";
}

export function isNationalRoom(room) {
  return ((room || {}).groupSettings || {}).autoKind === "national";
}

function roomsCol() {
  return fb.db.collection("chatRooms");
}

function millis(stamp) {
  if (!stamp) return 0;
  if (stamp.toMillis) return stamp.toMillis();
  if (stamp.seconds) return stamp.seconds * 1000;
  return new Date(stamp).getTime() || 0;
}

/**
 * 1:1 өрөөний дугаар. Аппын GlobalChatService.directRoomId-тэй ЯГ ижил
 * байх ёстой — эс бөгөөс нэг хос хүнд апп, вэб тус тусдаа өрөө үүсгэж,
 * яриа хоёр хуваагдана.
 */
export function directRoomId(a, b) {
  return `dm_${[a, b].sort().join("_")}`;
}

// ── Өрөө ────────────────────────────────────────────────────────────────────

/** My rooms: those with `_order` first by that number, the rest by newest message. */
export function watchMyRooms(myId, onChange) {
  return roomsCol()
    .where("participants", "array-contains", myId)
    .onSnapshot((snap) => {
      const rooms = [];
      snap.forEach((doc) => {
        const data = doc.data() || {};
        if (data.deleted === true) return;
        // Нийслэлийн мэргэжилтний групп хаагдсан — аппын chat_service.dart-
        // ийн isHiddenForRole-той ижил дүрэм.
        if (doc.id.startsWith("grp_city01")) return;
        rooms.push({ ...data, id: doc.id, ref: doc.ref });
      });
      rooms.sort((a, b) => {
        const orderA = typeof a._order === "number" ? a._order : Infinity;
        const orderB = typeof b._order === "number" ? b._order : Infinity;
        if (orderA !== orderB) return orderA - orderB;
        return millis(b.lastMessageTime) - millis(a.lastMessageTime);
      });
      onChange(rooms);
    });
}

/** Нэг өрөөний мессежүүд, хуучнаас нь. */
/**
 * Чат нээхэд өөрийн мөрөө members дэд цуглуулгад бичнэ. Аппын
 * ChatService.ownMemberDoc-той ижил зорилготой: группын гишүүдийн нэр,
 * сургууль өрөөний массивд биш, энд амьдарна.
 */
export async function touchMyMember(room, me) {
  if (!room || room.type === "direct") return;
  if (!(room.participants || []).includes(me.id)) return;
  try {
    await room.ref
      .collection("members")
      .doc(me.id)
      .set(
        {
          id: me.id,
          name: me.name,
          roleLabel: me.roleLabel || "Мэргэжилтэн",
          school: me.cityName || "",
          userPath: `teachers/${me.id}`,
          userRef: fb.db.doc(`teachers/${me.id}`),
          personId: me.personId || me.id,
          isGlobal: true,
        },
        { merge: true },
      );
  } catch (error) {
    console.log("touchMyMember", error);
  }
}

// Hidden by a superadmin, or a system note the app wrote for one person only.
function hiddenFromMe(data, myId) {
  if (data.hidden === true) return true;
  if (data.senderId !== "system") return false;
  if (data.targetUserId && data.targetUserId !== myId) return true;
  return !!(data.excludeUserId && data.excludeUserId === myId);
}

export function watchMessages(room, myId, onChange) {
  return room.ref
    .collection("messages")
    .orderBy("timestamp", "desc")
    .limit(200)
    .onSnapshot((snap) => {
      const messages = [];
      snap.forEach((doc) => {
        const data = doc.data() || {};
        if (hiddenFromMe(data, myId)) return;
        messages.push({ ...data, id: doc.id });
      });
      messages.reverse();
      onChange(messages);
    });
}

/** Messages from the start of `fromDay` to the end of `toDay`, both YYYY-MM-DD in local time. */
export function watchMessagesOn(room, myId, fromDay, toDay, onChange) {
  const start = new Date(`${fromDay}T00:00:00`);
  const end = new Date(`${toDay}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return room.ref
    .collection("messages")
    .where("timestamp", ">=", start)
    .where("timestamp", "<", end)
    .orderBy("timestamp")
    .onSnapshot((snap) => {
      const messages = [];
      snap.forEach((doc) => {
        const data = doc.data() || {};
        if (hiddenFromMe(data, myId)) return;
        messages.push({ ...data, id: doc.id });
      });
      onChange(messages);
    });
}

export function dayKey(date) {
  const two = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}`;
}

/** Day of the nearest message before `at`, or from `at` on when `newer`; "" when there is none. */
export async function nearestDay(room, myId, at, newer) {
  const snap = await room.ref
    .collection("messages")
    .where("timestamp", newer ? ">=" : "<", at)
    .orderBy("timestamp", newer ? "asc" : "desc")
    .limit(20)
    .get();
  const found = snap.docs
    .map((doc) => doc.data() || {})
    .find((data) => data.timestamp && !hiddenFromMe(data, myId));
  return found ? dayKey(found.timestamp.toDate()) : "";
}

export async function sendMessage(room, me, text, replyToId, mentions) {
  const body = String(text || "").trim();
  if (!body) return;

  const ref = await room.ref.collection("messages").add({
    content: body,
    senderId: me.id,
    senderName: me.name,
    type: "text",
    timestamp: fb.firestore.FieldValue.serverTimestamp(),
    readBy: { [me.id]: true },
    isDeleted: false,
    isEdited: false,
    delivered: true,
    ...(replyToId ? { replyToId } : {}),
    ...(mentions && mentions.length ? { mentions } : {}),
  });

  await room.ref.update({
    lastMessage: body,
    lastMessageTime: fb.firestore.FieldValue.serverTimestamp(),
    lastMessageSender: me.id,
    lastMessageId: ref.id,
  });
}

/** Уншсан гэж тэмдэглэнэ — аппын «харсан» тэмдэгтэй ижил талбарууд. */
export async function markRead(room, messages, myId) {
  const unread = (messages || []).filter(
    (message) =>
      message.senderId !== myId &&
      !(message.readBy && message.readBy[myId] === true),
  );
  if (!unread.length) return;

  const batch = fb.db.batch();
  for (const message of unread.slice(-200)) {
    batch.update(room.ref.collection("messages").doc(message.id), {
      [`readBy.${myId}`]: true,
      [`readAt.${myId}`]: fb.firestore.FieldValue.serverTimestamp(),
    });
  }
  batch.update(room.ref, {
    [`lastReadBy.${myId}`]: unread[unread.length - 1].id,
  });
  await batch.commit();
}

/** Уншаагүй мессежийн тоо — жагсаалтын тэмдэглэгээнд. */
export function watchUnread(room, myId, onChange) {
  return room.ref
    .collection("messages")
    .orderBy("timestamp", "desc")
    .limit(50)
    .onSnapshot((snap) => {
      let unread = 0;
      snap.forEach((doc) => {
        const data = doc.data() || {};
        if (data.senderId === myId) return;
        if (data.type === "system" || data.isDeleted === true) return;
        if (!data.readBy || data.readBy[myId] !== true) unread += 1;
      });
      onChange(unread);
    });
}

export async function deleteMessage(room, messageId) {
  await room.ref.collection("messages").doc(messageId).update({
    isDeleted: true,
    content: "",
    deletedAt: fb.firestore.FieldValue.serverTimestamp(),
  });
  await refreshLastMessage(room);
}

// Superadmin only: the message disappears for everyone, without a «deleted» placeholder.
export async function hideMessage(room, messageId, me) {
  await room.ref.collection("messages").doc(messageId).update({
    isDeleted: true,
    hidden: true,
    content: "",
    deletedAt: fb.firestore.FieldValue.serverTimestamp(),
    hiddenBy: me.id,
  });
  await refreshLastMessage(room);
}

// The chat list reads lastMessage from the room, so deleted or hidden text would stay there.
async function refreshLastMessage(room) {
  const recent = await room.ref
    .collection("messages")
    .orderBy("timestamp", "desc")
    .limit(20)
    .get();
  const doc = recent.docs.find((d) => (d.data() || {}).hidden !== true);
  if (!doc) {
    await room.ref.update({ lastMessage: "" });
    return;
  }
  const last = doc.data() || {};
  await room.ref.update({
    lastMessage:
      last.isDeleted === true ? "Устгагдсан мессеж" : String(last.content || ""),
    lastMessageTime: last.timestamp,
    lastMessageSender: last.senderId,
    lastMessageId: doc.id,
  });
}

export async function editMessage(room, messageId, content) {
  await room.ref.collection("messages").doc(messageId).update({
    content: content,
    isEdited: true,
    editedAt: fb.firestore.FieldValue.serverTimestamp(),
  });

  const newest = await room.ref
    .collection("messages")
    .orderBy("timestamp", "desc")
    .limit(1)
    .get();
  if (newest.empty || newest.docs[0].id !== messageId) return;
  await room.ref.update({ lastMessage: content });
}

/**
 * Үндэсний чатын админы бэлэн хариулт — мессеж дээр шошго болж суух.
 * `action` null бол тэмдэг арилна.
 */
export async function setMessageAction(room, messageId, me, action) {
  await room.ref
    .collection("messages")
    .doc(messageId)
    .update({
      action: action
        ? {
            id: action.id,
            mn: action.mn,
            en: action.en,
            at: new Date(),
            by: { id: me.id, name: me.name || "" },
          }
        : null,
    });
}

/** Same field as the app: reactions.{userId} = emoji, deleted when taken back. */
export async function setReaction(room, messageId, myId, emoji) {
  if (!myId) return;
  await room.ref.collection("messages").doc(messageId).update({
    ["reactions." + myId]: emoji || fb.firestore.FieldValue.delete(),
  });
}

/** Автоматаар үүсдэг өрөөг устгахгүй — дараагийн нэвтрэлтэд дахин үүснэ. */
export function isAutoRoom(room) {
  const settings = (room || {}).groupSettings || {};
  return settings.isDefaultGroup === true;
}

export async function deleteRoom(room, myId) {
  if (isAutoRoom(room)) return "Энэ чат автоматаар үүсдэг тул устгах боломжгүй";
  const admins = room.admins || [];
  if (room.type === "group" && !admins.includes(myId)) {
    return "Зөвхөн админ чатыг устгана";
  }
  await room.ref.update({
    deleted: true,
    deletedAt: fb.firestore.FieldValue.serverTimestamp(),
    deletedBy: myId,
  });
  return null;
}

// ── Шинэ чат ────────────────────────────────────────────────────────────────

/** Хоёр хүний чат. Дахин дарвал байгаа өрөөг нь нээнэ. */
export async function openDirect(me, person, year) {
  // Хуучин форматаар үүссэн өрөө байвал түүнийг л үргэлжлүүлнэ.
  const existing = await findDirectRoom(me.id, person.id);
  if (existing) {
    if (existing.deleted === true) {
      await existing.ref.update({
        deleted: false,
        deletedAt: null,
        deletedBy: null,
      });
    }
    return { ...existing, deleted: false };
  }

  const id = directRoomId(me.id, person.id);
  const ref = roomsCol().doc(id);
  const doc = await ref.get();

  if (doc.exists) {
    const data = doc.data() || {};
    if (data.deleted === true) {
      await ref.update({ deleted: false, deletedAt: null, deletedBy: null });
    }
    return { ...data, deleted: false, id: ref.id, ref };
  }

  const room = {
    type: "direct",
    userType: "citymanager",
    name: person.name,
    participants: [me.id, person.id],
    participantNames: { [me.id]: me.name, [person.id]: person.name },
    participantInfo: {
      [me.id]: myInfo(me),
      [person.id]: infoOf(person, year),
    },
    createdBy: me.id,
    createdAt: fb.firestore.FieldValue.serverTimestamp(),
    deleted: false,
  };
  await ref.set(room, { merge: true });
  return { ...room, id: ref.id, ref };
}

/** Хоёр хүний хоорондох өрөөг ямар ч дугаартай байсан олно. */
async function findDirectRoom(myId, otherId) {
  const snap = await roomsCol()
    .where("participants", "array-contains", myId)
    .get();

  for (const doc of snap.docs) {
    const data = doc.data() || {};
    const participants = data.participants || [];
    if (data.type !== "direct") continue;
    if (participants.length !== 2) continue;
    if (!participants.includes(otherId)) continue;
    return { ...data, id: doc.id, ref: doc.ref };
  }
  return null;
}

/** Олон хүнтэй групп — өөр өөр сургуулийн хүмүүс байж болно. */
export async function createGroup(me, people, name, year) {
  const participants = [me.id, ...people.map((person) => person.id)];
  const names = { [me.id]: me.name };
  const info = { [me.id]: myInfo(me) };
  for (const person of people) {
    names[person.id] = person.name;
    info[person.id] = infoOf(person, year);
  }

  const room = {
    type: "group",
    userType: "citymanager",
    name: String(name || "").trim() || "Групп чат",
    participants,
    participantNames: names,
    participantInfo: info,
    admins: [me.id],
    createdBy: me.id,
    createdAt: fb.firestore.FieldValue.serverTimestamp(),
    deleted: false,
    membersSeeded: true,
  };
  const ref = await roomsCol().add(room);

  // Гишүүдийн нэр, сургууль members дэд цуглуулгад — аппын жагсаалт эндээс
  // уншдаг.
  for (const id of participants) {
    const one = info[id] || {};
    await ref
      .collection("members")
      .doc(id)
      .set(
        {
          id: id,
          name: names[id] || "",
          roleLabel: one.role || "",
          school: one.school || "",
          userPath: one.userPath || "",
          personId: one.personId || id,
        },
        { merge: true },
      );
  }

  return { ...room, id: ref.id, ref };
}

/** Байгаа группд хүн нэмнэ. */
export async function addMembers(room, people, year) {
  const names = { ...(room.participantNames || {}) };
  const info = { ...(room.participantInfo || {}) };
  for (const person of people) {
    names[person.id] = person.name;
    info[person.id] = infoOf(person, year);
  }
  await room.ref.update({
    participants: fb.firestore.FieldValue.arrayUnion(
      ...people.map((person) => person.id),
    ),
    participantNames: names,
    participantInfo: info,
  });

  // Апп группын гишүүдийг members дэд цуглуулгаас уншдаг.
  for (const person of people) {
    const one = info[person.id] || {};
    await room.ref
      .collection("members")
      .doc(person.id)
      .set(
        {
          id: person.id,
          name: person.name,
          roleLabel: one.role || "",
          school: one.school || "",
          personId: one.personId || person.id,
        },
        { merge: true },
      );
  }
}

// ── Холбоо барих ────────────────────────────────────────────────────────────

/**
 * Хариуцсан сургуулиудын эрхтэй хүмүүс — захирал, менежер, нийгмийн
 * ажилтан, эмч, хоол зүйч, нягтлан. Багш, сурагчийг «шинэ чат» цонхноос
 * хайж олно, тэд хэт олон.
 */
const CONTACT_ROLES = [
  "schooldirector",
  "schoolmanager",
  "schoolsocialworker",
  "schooldoctor",
  "schoolmealcontroller",
  "schoolaccounter",
  "schoolaccounter2",
  // Хуучин бичлэгүүд том үсэгтэй байдаг — аппын жагсаалттай ижил.
  "schoolDirector",
  "schoolManager",
  "SchoolDirector",
  "SchoolManager",
];

/**
 * Бусад мэргэжилтэн — root `teachers` дотор. Аппын GlobalChatService
 * _otherCityManagers-тэй яг ижил дүрэм: аймгаар шүүхгүй, өөрийгөө болон
 * устгасныг хасна, дэд бичиг нь «аймаг · албан тушаал».
 */
async function loadSpecialists(myId) {
  try {
    const snap = await fb.db.collection("teachers").get();
    return snap.docs
      .filter((doc) => {
        const data = doc.data() || {};
        if (doc.id === myId) return false;
        if (data.deleted === true) return false;
        return String(data.role || "").toLowerCase() === "citymanager";
      })
      .map((doc) => {
        const data = doc.data() || {};
        const last = data.lastName || data.LAST_NAME || "";
        const first = data.firstName || data.FIRST_NAME || "";
        // Аль аймгийнх нь бүх жагсаалтад харагдана (апптай ижил).
        const picked = data.cityCode2;
        const cityName =
          picked && picked.name
            ? String(picked.name)
            : data.cityCode
              ? `Аймаг (${data.cityCode})`
              : "";
        const duty = String(data.duty || data.position || "").trim();
        return {
          id: doc.id,
          name: `${last} ${first}`.trim() || data.DISPLAY_NAME || "Нэргүй",
          role: "citymanager",
          photoURL: data.photoURL || "",
          personId: data.PERSON_ID || "",
          schoolName: [cityName, duty].filter(Boolean).join(" · "),
          schoolPath: "",
          isSpecialist: true,
        };
      });
  } catch (e) {
    return [];
  }
}

export async function loadContacts(cityCode, myId) {
  const schools = await loadSchools(cityCode);
  if (!schools.length) return [];

  const perSchool = await Promise.all(
    schools.map(async (school) => {
      try {
        // Firestore-ийн compat SDK нь `in`-д 10 хүртэл утга авдаг тул
        // жагсаалтаа хувааж асууна — эс бөгөөс query унаж, тухайн
        // сургуулийн хүмүүс бүгд алга болно.
        const chunks = [];
        for (let i = 0; i < CONTACT_ROLES.length; i += 10) {
          chunks.push(CONTACT_ROLES.slice(i, i + 10));
        }

        const snaps = await Promise.all(
          chunks.map((roles) =>
            school.ref.collection("teachers").where("role", "in", roles).get(),
          ),
        );

        const snap = { docs: snaps.flatMap((one) => one.docs) };

        return snap.docs
          .filter((doc) => {
            const data = doc.data() || {};
            return data.deleted !== true && data.moved !== true;
          })
          .map((doc) => {
            const data = doc.data() || {};
            const last = data.lastName || data.LAST_NAME || "";
            const first = data.firstName || data.FIRST_NAME || "";
            return {
              id: doc.id,
              name:
                `${last} ${first}`.trim() || data.DISPLAY_NAME || "Нэргүй",
              role: String(data.role || "").toLowerCase(),
              photoURL: data.photoURL || "",
              personId: data.PERSON_ID || "",
              schoolName: school.name,
              schoolPath: school.ref.path,
            };
          });
      } catch (e) {
        return [];
      }
    }),
  );

  const specialists = await loadSpecialists(myId);

  // Аппын жагсаалттай ижил дараалал: эхлээд сургуульгүй мэргэжилтнүүд,
  // дараа нь сургууль тус бүрээр, дотор нь нэрээр.
  return [...specialists, ...perSchool.flat()].sort((a, b) => {
    const bySchool = String(a.schoolName).localeCompare(String(b.schoolName));
    return bySchool !== 0
      ? bySchool
      : String(a.name).localeCompare(String(b.name));
  });
}

/** Мэргэжилтний хариуцсан сургуулиуд. */
export async function loadSchools(cityCode) {
  if (!cityCode) return [];
  const snap = await fb.db
    .collection("schools")
    .where("cityCode", "==", String(cityCode))
    .get();

  return snap.docs
    .filter((doc) => (doc.data() || {}).deleted !== true)
    .map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      name: (doc.data() || {}).name || doc.id,
    }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/** Нэг сургуулийн эрхтэй хүмүүс — захирал, менежер, ажилтан, багш. */
export async function loadStaff(school) {
  const snap = await school.ref
    .collection("teachers")
    .where("role", "in", STAFF_ROLES)
    .get();

  return snap.docs
    .filter((doc) => {
      const data = doc.data() || {};
      return data.deleted !== true && data.moved !== true;
    })
    .map((doc) => {
      const data = doc.data() || {};
      const last = data.lastName || data.LAST_NAME || "";
      const first = data.firstName || data.FIRST_NAME || "";
      const name = `${last} ${first}`.trim() || data.DISPLAY_NAME || "Нэргүй";
      return {
        id: doc.id,
        name,
        role: String(data.role || "").toLowerCase(),
        photoURL: data.photoURL || "",
        personId: data.PERSON_ID || "",
        schoolName: school.name,
        schoolPath: school.ref.path,
      };
    })
    .sort((a, b) => {
      const byRole =
        STAFF_ROLES.indexOf(a.role) - STAFF_ROLES.indexOf(b.role);
      return byRole !== 0 ? byRole : String(a.name).localeCompare(String(b.name));
    });
}

/** Нэг сургуулийн багш нар. */
export async function loadTeachers(school) {
  const snap = await school.ref
    .collection("teachers")
    .where("role", "==", "teacher")
    .get();

  return snap.docs
    .filter((doc) => {
      const data = doc.data() || {};
      return data.deleted !== true && data.moved !== true;
    })
    .map((doc) => {
      const data = doc.data() || {};
      const last = data.lastName || data.LAST_NAME || "";
      const first = data.firstName || data.FIRST_NAME || "";
      return {
        id: doc.id,
        name: `${last} ${first}`.trim() || data.DISPLAY_NAME || "Нэргүй",
        role: "teacher",
        photoURL: data.photoURL || "",
        personId: data.PERSON_ID || "",
        schoolName: school.name,
        schoolPath: school.ref.path,
      };
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/** Сургуулийн ангиуд — сурагч сонгохын өмнө бүлгээ сонгоно. */
export async function loadClassNames(school, year) {
  const snap = await school.ref.collection(`students-${year}`).get();
  const names = new Set();
  snap.forEach((doc) => {
    const data = doc.data() || {};
    if (data.deleted === true || data.moved === true) return;
    const name = String(data.STUDENT_GROUP_NAME || "").trim();
    if (name) names.add(name);
  });
  return [...names].sort((a, b) => a.localeCompare(b));
}

/** Бусад мэргэжилтэн — жагсаалтад сонгохоор. */
export async function loadSpecialistList(myId) {
  return loadSpecialists(myId);
}

/** Нэг сургуулийн сурагчид — тухайн хичээлийн жилээр. */
export async function loadStudents(school, year, className) {
  // Бүлгээ сонгосон бол зөвхөн түүнийг асууна — 400-гийн хязгаарт
  // багтахгүй том сургуульд сурагч алга болохгүй (апптай ижил).
  let query = school.ref.collection(`students-${year}`);
  if (className) {
    query = query.where("STUDENT_GROUP_NAME", "==", className);
  }
  const snap = await query.limit(400).get();

  return snap.docs
    .filter((doc) => {
      const data = doc.data() || {};
      return data.deleted !== true && data.moved !== true;
    })
    .map((doc) => {
      const data = doc.data() || {};
      const name = `${data.LAST_NAME || ""} ${data.FIRST_NAME || ""}`.trim();
      return {
        id: doc.id,
        name: name || "Нэргүй",
        role: "student",
        photoURL: data.photoURL || "",
        personId: data.PERSON_ID || "",
        schoolName: school.name,
        schoolPath: school.ref.path,
        className: data.STUDENT_GROUP_NAME || "",
      };
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}
